import { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "config/api";
import PropTypes from "prop-types";

// @mui
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";

// ZionLadder UI
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

function PricingSection({
  userName,
  expireAt,
  hasEverPurchased,
  isLoggedIn,
  isUserInfo,
  paymentRoute,
  returnTo,
  targetUserPk,
}) {
  const { t, i18n } = useTranslation();

  // 用户上下文信息：
  // - userName: 当前用户名称（用于在定价区上方展示欢迎语或状态）
  // - expireAt: 当前套餐到期时间（ISO 字符串或 null，null 表示从未开通过）
  // - hasEverPurchased: 是否曾经购买过任意套餐（用于区分“首次开通”和“重新开通”）
  // 当前版本仅接收这些参数，具体展示逻辑可在后续迭代中接入。
  // eslint-disable-next-line no-unused-vars
  const userContext = { userName, expireAt, hasEverPurchased, isLoggedIn, isUserInfo };

  const [plans, setPlans] = useState([]);

  const navigate = useNavigate();

  const buildPaymentState = (plan) => ({
    shop_pk: plan?.id ?? null,
    username: userName || "",
    ...(targetUserPk ? { target_user_pk: targetUserPk } : {}),
    // UI-only fields (do not trust these on backend)
    title: plan?.title || "",
    duration: plan?.duration || "",
    total_price: plan?.totalPrice || "",
    total_old_price: plan?.totalOldPrice || "",
    return_to: returnTo || "/userinfo/#pricing",
  });

  const persistPendingPayment = (state) => {
    try {
      sessionStorage.setItem("pending_payment", JSON.stringify(state));
    } catch (e) {
      // ignore
    }
  };

  // 根据当前登录状态和套餐状态，计算统一的按钮文案
  const computeCtaLabel = () => {
    // 场景 1：未登录（无论是否曾经购买过，在首页都统一展示“立即开通”，点击后跳登录）
    if (!isLoggedIn) {
      return t("pricing.labels.cta.open");
    }

    // 场景 2：已登录但当前组件作为首页/非用户中心使用时，统一引导去“查看我的套餐”
    if (!isUserInfo) {
      return t("pricing.labels.cta.toUserCenter");
    }

    // 下面的逻辑用于“用户中心”等已拿到真实用户状态的场景

    // 规范化有效期（过滤掉 1970-01-01 等“未开通”占位时间）
    const hasValidExpire =
      !!expireAt && !expireAt.startsWith("1970-01-01") && !expireAt.startsWith("1970-1-1");

    let isExpired = false;
    if (hasValidExpire) {
      const exp = new Date(expireAt);
      if (!Number.isNaN(exp.getTime())) {
        isExpired = exp < new Date();
      }
    }

    // 从未购买或没有有效期（包括 1970-01-01）
    if (!hasEverPurchased || !hasValidExpire) {
      return t("pricing.labels.cta.open");
    }

    // 曾经购买且已过期
    if (isExpired) {
      return t("pricing.labels.cta.reopen");
    }

    // 曾经购买且未过期
    return t("pricing.labels.cta.renew");
  };

  const handleOpenPayment = (plan) => {
    const paymentState = buildPaymentState(plan);

    // 未登录：跳转到登录页面；用 sessionStorage 临时保存支付信息
    if (!isLoggedIn) {
      persistPendingPayment(paymentState);
      window.location.href = `/signin/?next=${encodeURIComponent("/payment/")}`;
      return;
    }

    // 已登录但当前组件作为首页/非用户中心使用：引导用户前往用户中心查看/管理自己的套餐
    if (!isUserInfo) {
      window.location.href = "/userinfo/#pricing";
      return;
    }

    // 在用户中心等有完整用户状态的场景下，进入独立支付页面（state 传参）
    persistPendingPayment(paymentState); // 兜底：避免刷新/意外丢 state
    navigate(paymentRoute || "/payment/", { state: paymentState });
  };

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/web2/pricing/`); // 根据后端路由调整
        const data = res.data;
        const pricingFromApi = (data.pricing || []).map((item) => {
          const durationDisplay =
            i18n.language && i18n.language.startsWith("zh") && item.duration_display_zh
              ? item.duration_display_zh
              : item.duration_display_en || item.duration_display_zh || "";

          const money = typeof item.money === "number" ? item.money : parseFloat(item.money || 0);
          const oldMoney =
            typeof item.old_money === "number" ? item.old_money : parseFloat(item.old_money || 0);

          // 计算套餐对应的月数
          // 规则：
          // - 如果 duration === 365，则视为 12 个月
          // - 否则使用 unit（代表月数）
          let months = 1;

          if (item.duration === 365) {
            months = 12;
          } else if (typeof item.unit === "number") {
            months = item.unit || 1;
          } else if (typeof item.unit === "string") {
            const parsed = parseFloat(item.unit);
            months = Number.isNaN(parsed) || parsed <= 0 ? 1 : parsed;
          }

          const pricePerMonth = months > 0 ? money / months : money;
          const oldPricePerMonth = oldMoney ? (months > 0 ? oldMoney / months : oldMoney) : 0;

          return {
            id: item.id,
            highlight: !!item.marked,
            tag: item.marked ? t("pricing.labels.recommended") : "",
            title: item.name,
            duration: durationDisplay,
            price: `€${pricePerMonth.toFixed(2)}`,
            totalPrice: `€${money.toFixed(2)}`,
            totalOldPrice: oldMoney ? `€${oldMoney.toFixed(2)}` : "",
            oldPrice: oldPricePerMonth ? `€${oldPricePerMonth.toFixed(2)}` : "",
            discount: item.saved_rate
              ? t("pricing.labels.savePercent", { rate: item.saved_rate })
              : "",
            features: [],
          };
        });

        setPlans(pricingFromApi);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to load pricing:", error);
      }
    };

    fetchPricing();
  }, [t, i18n.language]);

  return (
    <MKBox component="section" id="pricing" py={{ xs: 6, md: 10 }}>
      <Container>
        {/* 标题区 */}
        <MKBox textAlign="center" mb={6}>
          <MKTypography variant="h3" fontWeight="bold" mb={1}>
            {t("pricing.sectionTitle")}
          </MKTypography>
          <MKTypography variant="body2" color="text">
            {t("pricing.sectionSubtitle")}
          </MKTypography>
        </MKBox>

        {/* 三列套餐卡片 */}
        <Grid container spacing={3} justifyContent="center">
          {plans.map((plan) => (
            <Grid item xs={12} sm={6} md={6} lg={3} key={plan.id}>
              <Card
                sx={({ palette, boxShadows }) => ({
                  height: "100%",
                  position: "relative",
                  p: 2.25,
                  borderRadius: "xl",
                  boxShadow: plan.highlight ? boxShadows.xl : boxShadows.md,
                  border: plan.highlight
                    ? `2px solid ${palette.success.main}`
                    : `1px solid ${palette.grey[200]}`,
                  transform: plan.highlight ? "translateY(-6px)" : "none",
                  transition: "all 200ms ease",
                  "&:hover": {
                    boxShadow: boxShadows.xxl,
                    transform: plan.highlight ? "translateY(-10px)" : "translateY(-4px)",
                  },
                })}
              >
                {/* 角标 */}
                {plan.highlight && (
                  <MKBox
                    px={1.5}
                    py={0.5}
                    bgColor="success"
                    color="white"
                    borderRadius="lg"
                    fontSize="0.75rem"
                    position="absolute"
                    top={16}
                    left="50%"
                    sx={{ transform: "translateX(-50%)" }}
                  >
                    {plan.tag}
                  </MKBox>
                )}

                {/* 标题 & 时长 */}
                <MKBox textAlign="center" mt={plan.highlight ? 3 : 1} mb={1.25}>
                  <MKTypography variant="h6" fontWeight="bold">
                    {plan.title}
                  </MKTypography>
                  <MKTypography variant="button" color="text" mt={0.5}>
                    {plan.duration}
                  </MKTypography>
                </MKBox>

                <Divider />

                {/* 价格区 */}
                <MKBox textAlign="center" mt={2} mb={1.5}>
                  <MKTypography
                    variant="h3"
                    fontWeight="bold"
                    color={plan.highlight ? "success" : "dark"}
                  >
                    {plan.price}
                    <MKTypography
                      component="span"
                      variant="button"
                      color="text"
                      sx={{ ml: 0.5, verticalAlign: "middle" }}
                    >
                      / {t("pricing.labels.perMonth")}
                    </MKTypography>
                  </MKTypography>
                  <MKTypography
                    variant="button"
                    color="text"
                    sx={{ textDecoration: "line-through", opacity: 0.6 }}
                  >
                    {plan.oldPrice} / {t("pricing.labels.perMonth")}
                  </MKTypography>
                  <MKTypography variant="button" color="success" display="block" mt={0.5}>
                    {plan.discount}
                  </MKTypography>
                </MKBox>

                {/* 产品说明 */}
                <MKBox mt={1} mb={2}>
                  <MKTypography
                    variant="caption"
                    color="text"
                    textAlign="center"
                    mb={0.5}
                    display="block"
                  >
                    {t("pricing.features.bandwidth")}
                  </MKTypography>
                  <MKTypography
                    variant="caption"
                    color="text"
                    textAlign="center"
                    mb={0.5}
                    display="block"
                  >
                    {t("pricing.features.multiDevice")}
                  </MKTypography>
                  <MKTypography
                    variant="caption"
                    color="error"
                    fontWeight="bold"
                    textAlign="center"
                    mb={0.5}
                    display="block"
                  >
                    {t("pricing.features.noShare")}
                  </MKTypography>
                  <MKTypography variant="caption" color="text" textAlign="center" display="block">
                    {t("pricing.features.streamingUnlock")}
                  </MKTypography>
                </MKBox>

                {/* 总价显示 */}
                <MKBox textAlign="center" mb={1}>
                  <MKTypography variant="button" color="dark" fontWeight="bold">
                    {plan.totalPrice}
                  </MKTypography>
                  {plan.totalOldPrice && (
                    <MKTypography
                      variant="caption"
                      color="text"
                      sx={{ textDecoration: "line-through", opacity: 0.6, ml: 1 }}
                    >
                      {plan.totalOldPrice}
                    </MKTypography>
                  )}
                </MKBox>

                {/* 按钮 */}
                <MKBox textAlign="center">
                  <MKButton
                    variant={plan.highlight ? "contained" : "outlined"}
                    color="success"
                    size="large"
                    fullWidth
                    onClick={() => handleOpenPayment(plan)}
                  >
                    {computeCtaLabel()}
                  </MKButton>
                </MKBox>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* 价格说明：移动到价格表下方 */}
        <MKBox textAlign="center" mt={4}>
          <MKTypography variant="button" color="success" fontWeight="bold" display="block">
            {t("pricing.priceNote1")} · {t("pricing.priceNote2")}
          </MKTypography>
          <MKTypography
            variant="caption"
            color="text"
            fontWeight="medium"
            display="block"
            mt={0.75}
          >
            {t("pricing.priceNote3")}
          </MKTypography>
        </MKBox>
      </Container>
    </MKBox>
  );
}

PricingSection.propTypes = {
  // 当前登录用户名，用于在定价区展示个性化文案（可选）
  userName: PropTypes.string,
  // 当前套餐到期时间（ISO8601 字符串），如果从未开通过可以传 null 或 undefined
  expireAt: PropTypes.string,
  // 是否曾经购买过任意套餐，用于区分“首次开通”和“重新开通”文案
  hasEverPurchased: PropTypes.bool,
  // 当前是否已登录，用于决定首页点击按钮的跳转行为
  isLoggedIn: PropTypes.bool,
  // 当前组件是否作为用户中心页面使用，用于区分首页“查看我的套餐”场景
  isUserInfo: PropTypes.bool,
  // 支付页路由，默认 /payment/
  paymentRoute: PropTypes.string,
  // 支付成功返回路径，默认 /userinfo/#pricing
  returnTo: PropTypes.string,
  // 公共充值页可透传目标用户 pk（不展示，仅用于后续流程）
  targetUserPk: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

PricingSection.defaultProps = {
  userName: "",
  expireAt: null,
  hasEverPurchased: false,
  isLoggedIn: false,
  isUserInfo: false,
  paymentRoute: "/payment/",
  returnTo: "/userinfo/#pricing",
  targetUserPk: null,
};

export default PricingSection;
